import React, { MouseEvent, useRef, useState } from "react";
import HomeButtonIcon from "@src/assets/svg/home-button.svg";
import activeLedIcon from "@src/assets/svg/active-led.svg";
import styles from "./taskbar.module.scss";
import { getNodeManager } from "@src/cloud-connect/dependency-injection-container/get-node-manager";
import { useGetUserTickets } from "./use-get-user-tickets";
import { TicketsTaskbarCard } from "@src/components/molecules/tickets-taskbar-cards/tickets-taskbar-card";
import { patchTicketStatusUseCase } from "@src/cloud-connect/support/use-cases/patch-ticket-status-use-case";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { useI18N } from "@src/language/language-context";
import addTicketIcon from "@src/assets/svg/add-ticket.svg";
import historyIcon from "@src/assets/svg/history-icon-blue.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  updateAddRequestTechnicalSupportDrawerModalWithoutMachine,
  updateModalsCloseDrawerModal,
} from "@src/reducers/your-reducer";
import { RootState } from "@src/reducers/store";
import { VerticalSeparator } from "@src/components/atoms/vertical-separator/vertical-separator";
import { TaskbarTicketsHistoryPopover } from "@src/components/molecules/taskbar-tickets-history-popover/taskbar-tickets-history-popover";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";
import { useSSEUnavailable } from "@src/cloud-connect/sse/use-sse-unavailable";

const cssClassName = "taskbar";

export default function Taskbar() {
  const i18n = useI18N();
  const dispatch = useDispatch();
  const historyWrapperRef = useRef<HTMLDivElement | null>(null);
  const historyButtonRef = useRef<HTMLButtonElement | null>(null);
  const pendingCloseTicketIdsRef = useRef<Set<string>>(new Set());
  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const [tickets, setTickets] = useGetUserTickets();
  const [patchLoadingIds, setPatchLoadingIds] = useState<string[]>([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const isSupportMarkDoneDisabled = useSSEUnavailable("service-support");

  const texts = {
    errorPatchingTicket: i18n("modules.taskbar.errorPatchingTicket"),
    homeIconAltText: i18n("modules.taskbar.homeIconAltText"),
    addTicketIconAltText: i18n("modules.taskbar.addTicketIconAltText"),
    historyIconAltText: i18n("modules.taskbar.historyIconAltText"),
    reconnectingActionsUnavailableTooltip: i18n(
      "modules.sse.reconnectingActionsUnavailableTooltip",
    ),
  };

  // ticketing features get deactivated for namespace a6f962a7-77f7-46f2-9c72-59580255fdf8 (controlled via the "fakuma-hide-ticketing" feature flag)
  const isTicketingFeatureEnabled = useFeatureFlag("fakuma-hide-ticketing");
  const supportMarkDoneDisabledTooltip = isSupportMarkDoneDisabled
    ? texts.reconnectingActionsUnavailableTooltip
    : "";

  const handleHomeButtonClick = (e: MouseEvent) => {
    e.preventDefault();
    getNodeManager().performChangesCheckWhenNavigatingAwayFromNodeManager("/");
  };

  const handleAddTicketClick = () => {
    dispatch(updateAddRequestTechnicalSupportDrawerModalWithoutMachine({}));
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "supportChatDrawerModal",
      }),
    );
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: "addRequestTechnicalSupportDrawerModalWithoutMachine",
      }),
    );
  };

  const handleCloseIconClick = async (e: MouseEvent, ticketId: string) => {
    e.preventDefault();
    if (isSupportMarkDoneDisabled) return;
    if (pendingCloseTicketIdsRef.current.has(ticketId)) return;
    pendingCloseTicketIdsRef.current.add(ticketId);

    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "supportChatDrawerModal",
      }),
    );

    setPatchLoadingIds((prev) => [...prev, ticketId]);

    try {
      const response = await patchTicketStatusUseCase(ticketId, "solved");

      if (!response.success) {
        errorSnackbar(response.error || texts.errorPatchingTicket);
      } else {
        setTickets((prev) => prev.filter((t) => t.id !== ticketId));
      }
    } finally {
      setPatchLoadingIds((prev) => prev.filter((id) => id !== ticketId));
      pendingCloseTicketIdsRef.current.delete(ticketId);
    }
  };

  const handleToggleTicketHistory = () => {
    setIsHistoryOpen((prev) => !prev);
  };

  return (
    <div className={styles[cssClassName]}>
      <div className={styles[`${cssClassName}__home-button-wrapper`]}>
        <button
          className={styles[`${cssClassName}__home-button`]}
          onClick={handleHomeButtonClick}
        >
          <img src={HomeButtonIcon} alt={texts.homeIconAltText} />
        </button>
        <img
          className={styles[`${cssClassName}__home-button-led`]}
          src={activeLedIcon}
          alt={texts.homeIconAltText}
        />
      </div>

      {authContext &&
        (authContext?.permissions.includes("view-all-tickets") ||
          authContext?.permissions.includes("view-own-tickets")) && (
          <div
            className={styles[`${cssClassName}__right-container`]}
            ref={historyWrapperRef}
          >
            {tickets.length > 3 && (
              <button
                ref={historyButtonRef}
                className={styles[`${cssClassName}__tickets-button`]}
                onClick={handleToggleTicketHistory}
              >
                <img src={historyIcon} alt={texts.historyIconAltText} />
              </button>
            )}
            {isHistoryOpen && (
              <TaskbarTicketsHistoryPopover
                tickets={tickets}
                patchLoadingIds={patchLoadingIds}
                handleCloseIconClick={handleCloseIconClick}
                handleToggleTicketHistory={handleToggleTicketHistory}
                wrapperRef={historyWrapperRef}
                isMarkAsDoneDisabled={isSupportMarkDoneDisabled}
                disabledMarkAsDoneTooltip={supportMarkDoneDisabledTooltip}
              />
            )}
            {tickets.length > 3 && (
              <VerticalSeparator style={{ backgroundColor: "#3aacc8" }} />
            )}
            <div className={styles[`${cssClassName}__tickets-wrapper`]}>
              {tickets.length > 0 &&
                tickets
                  .slice(0, 3)
                  .map((ticket) => (
                    <TicketsTaskbarCard
                      key={ticket.id}
                      ticket={ticket}
                      patchLoading={patchLoadingIds.includes(ticket.id)}
                      handleCloseIconClick={handleCloseIconClick}
                      isMarkAsDoneDisabled={isSupportMarkDoneDisabled}
                      disabledMarkAsDoneTooltip={supportMarkDoneDisabledTooltip}
                    />
                  ))}
            </div>
            {authContext &&
              authContext?.permissions.includes("create-tickets") &&
              isTicketingFeatureEnabled && (
                <button
                  className={styles[`${cssClassName}__add-ticket-button`]}
                  onClick={handleAddTicketClick}
                >
                  <img src={addTicketIcon} alt={texts.addTicketIconAltText} />
                </button>
              )}
          </div>
        )}
    </div>
  );
}
